import { Button, useToast } from '@sl/react-datafabric-components';
import { Menu } from 'primereact/menu';
import { MenuItem, MenuItemOptions } from 'primereact/menuitem';
import { useRef, useState, type FC } from 'react';
import {
  useCreateBillingPortalSessionMutation,
  useReactivateSubscriptionMutation,
  useRetryPaymentMutation,
} from '../../../../common/redux/services/subscriptions';
import getErrorMessage from '../../../../common/utils/get-error-message';
import { useDeleteSubscriptionWithConfirmation } from '../components/use-delete-company-subscription-with-confirmation';
import { BuyTokensDialog } from './buy-tokens-success-dialog';

const itemRenderer = (
  item: Omit<MenuItem, 'data'> & { data?: { color?: string } },
  options: MenuItemOptions
) => (
  <div className='p-menuitem-content' onClick={(e) => options.onClick(e)}>
    <a className='flex align-items-center p-menuitem-link'>
      <span className={`${item.icon}  ${item.data?.color ?? 'text-700'}`} />
      <span className={`mx-2 ${item.data?.color ?? 'text-700'}`}>
        {item.label}
      </span>
    </a>
  </div>
);

interface SubscriptionOptionsMenuProps {
  rowData: any; // Pass rowData from DataGrid
}

export const SubscriptionOptionsMenu: FC<SubscriptionOptionsMenuProps> = ({
  rowData,
}) => {
  const menu = useRef<Menu>(null);
  const { showToast } = useToast();
  const [isBuyTokensDialogOpen, setIsBuyTokensDialogOpen] = useState(false);

  const [createBillingPortalSession, { isLoading: isCreatingSession }] =
    useCreateBillingPortalSessionMutation();
  const [isRetryingPayment, setIsRetryingPayment] = useState(false);
  const [isReactivating, setisReactivating] = useState(false);

  const { deleteSubscriptionWithConfirmation } =
    useDeleteSubscriptionWithConfirmation();

  const [retryPayment] = useRetryPaymentMutation();
  const [reactivateSubscription] = useReactivateSubscriptionMutation();

  const onUpdate = async () => {
    if (!rowData.providerMetadata.customerId) {
      showToast(
        {
          severity: 'error',
          summary: 'Error',
          detail: 'Customer ID not found for this subscription.',
        },
        'top-right'
      );
      return;
    }
    try {
      const response = await createBillingPortalSession({
        customerId: rowData.providerMetadata.customerId,
      }).unwrap();
      if (response.url) {
        window.location.href = response.url; // Redirect to Stripe's billing portal
      } else {
        showToast(
          {
            severity: 'error',
            summary: 'Error',
            detail: 'Could not retrieve billing portal URL.',
          },
          'top-right'
        );
      }
    } catch (error) {
      showToast(
        {
          severity: 'error',
          summary: 'Error',
          detail: getErrorMessage(error) || 'Failed to open billing portal.',
        },
        'top-right'
      );
    }
  };

  const onRetry = async () => {
    setIsRetryingPayment(true);
    try {
      const response = await retryPayment(rowData.id).unwrap();
      // Assuming the status might change, you might want to refetch data or rely on optimistic updates
      showToast(
        {
          severity: 'success',
          summary: 'Done',
          detail: 'Retry successful!',
        },
        'top-right'
      );
    } catch (error) {
      showToast(
        {
          severity: 'error',
          summary: 'Error',
          detail: getErrorMessage(error) || 'Failed to retry payment.',
        },
        'top-right'
      );
    } finally {
      setIsRetryingPayment(false);
    }
  };

  const onReactivate = async () => {
    setisReactivating(true);
    try {
      const response = await reactivateSubscription(rowData.id).unwrap();
      // Assuming the status might change, you might want to refetch data or rely on optimistic updates
      showToast(
        {
          severity: 'success',
          summary: 'Done',
          detail: 'Reactivation successful!',
        },
        'top-right'
      );
    } catch (error) {
      showToast(
        {
          severity: 'error',
          summary: 'Error',
          detail:
            getErrorMessage(error) || 'Failed to reactivate subscription.',
        },
        'top-right'
      );
    } finally {
      setisReactivating(false);
    }
  };

  const onDelete = () => {
    deleteSubscriptionWithConfirmation(rowData, {
      onSuccess: () =>
        showToast(
          {
            severity: 'success',
            summary: 'Done',
            detail:
              'You have unsubscribed. You have access until the end of the cycle.',
          },
          'top-right'
        ),
      onError: (error) => {
        showToast(
          {
            severity: 'error',
            summary: 'Error',
            detail: error?.data?.message,
          },
          'bottom-right'
        );
      },
    });
  };

  const onBuyTokens = async () => {
    setIsBuyTokensDialogOpen(true);
  };

  const reactivation = rowData.endDate;
  const actionItems: MenuItem[] = [
    ...[
      {
        label: 'Buy credits',
        icon: 'pi pi-ticket',
        template: itemRenderer,
        command: onBuyTokens,
      },
      {
        label: 'Update billing details',
        icon: 'pi pi-credit-card',
        template: itemRenderer,
        command: onUpdate,
      },
      {
        label: reactivation ? 'Reactivate' : 'Unsubscribe',
        icon: `pi pi-${reactivation ? 'refresh' : 'ban'}`,
        template: itemRenderer,
        command: reactivation ? onReactivate : onDelete,
        data: {
          color: 'text-red-500',
        },
      },
      ...(rowData.status === 'paymentFailed'
        ? [
            {
              label: 'Retry Payment',
              icon: isRetryingPayment
                ? 'pi pi-spin pi-spinner'
                : 'pi pi-replay',
              template: itemRenderer,
              command: onRetry,
              data: { color: 'text-orange-500' },
              disabled: isRetryingPayment,
            },
          ]
        : []),
    ],
  ];

  return (
    <>
      <Menu
        model={actionItems}
        popup
        ref={menu}
        id='popup_menu_right'
        popupAlignment='right'
      />
      <div className='flex flex-nowrap'>
        <Button
          severity='secondary'
          outlined
          rounded
          icon='pi pi-ellipsis-v'
          className='border-300'
          onClick={(event) => menu.current?.toggle(event)}
          loading={isCreatingSession} // Keep existing loading for other actions if needed
          aria-controls='popup_menu_right'
          aria-haspopup
          disabled={rowData?.plan?.trial}
        />
        {isBuyTokensDialogOpen && (
          <BuyTokensDialog
            visible={isBuyTokensDialogOpen}
            onHide={() => setIsBuyTokensDialogOpen(false)}
            subscriptionData={rowData}
          />
        )}
      </div>
    </>
  );
};
